import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { Backend, DataSearchResult } from '../enums/enums'

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    withCredentials: true,
  }

  constructor(
    private http: HttpClient,
    private logger: DdcLoggerService,
  ) {}

  /**
   * Search words using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchWords(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchWords()', query, maxResults)

    // Limit max rows to 10
    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.WORD}search/?max=${maxResults}&word=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchWords() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchWords() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search pairs using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchPairs(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchPairs()', query, maxResults)

    // Limit max rows to 10
    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.PAIR}search/?max=${maxResults}&word=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchPairs() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchPairs() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search dictionaries using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchDictionaries(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchDictionaries()', query, maxResults)

    // Limit max rows to 10
    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.DIC}search/?max=${maxResults}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchDictionaries() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchDictionaries() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search users using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchUsers(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchUsers()', query, maxResults)

    // Limit max rows to 10
    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.RELATIONSHIP_SEARCH}?max=${maxResults}&user=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchUsers() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchUsers() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search categories using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchCategories(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchCategories()', query, maxResults)

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.CATEGORY}search/?label=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchCategories() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchCategories() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search tags using the backend search endpoint
   * @param query Search query
   * @param maxResults Maximum number of results to return
   * @returns Observable<DataSearchResult[]>
   */
  searchTags(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchTags()', query, maxResults)

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.TAG}search/?label=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchTags() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchTags() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search notes using the backend search endpoint
   */
  searchNotes(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchNotes()', query, maxResults)

    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.NOTE}search/?max=${maxResults}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchNotes() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchNotes() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search quizzes using the backend search endpoint
   */
  searchQuizzes(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchQuizzes()', query, maxResults)

    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.QUIZ}search/?max=${maxResults}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchQuizzes() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchQuizzes() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search figures using the backend search endpoint
   */
  searchFigures(query: string, maxResults: number = 10): Observable<DataSearchResult[]> {
    this.logger.info('SearchService: searchFigures()', query, maxResults)

    if (maxResults > 10 || maxResults < 1) maxResults = 10

    return this.http
      .get<DataSearchResult[]>(
        `${Backend.HOST_CONTAINER + Backend.FIGURE}search/?max=${maxResults}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchFigures() - success', res)
          return res
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchFigures() - error:', errorMessage)
          return of([])
        }),
      )
  }

  /**
   * Search words with pagination support
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of results per page
   * @param filters Optional filters to apply
   * @returns Observable<DataSearchResult[]>
   */
  searchWordsWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: any,
  ): Observable<any> {
    this.logger.info('SearchService: searchWordsWithPagination()', query, page, pageSize, filters)

    // Build query parameters
    let queryParams = `page=${page}&pageSize=${pageSize}&word=${query}`

    // Add filters if provided
    if (filters) {
      if (filters.language) queryParams += `&language=${filters.language}`
      if (filters.article) queryParams += `&article=${filters.article}`
      if (filters.partOfSpeech) queryParams += `&partOfSpeech=${filters.partOfSpeech}`
    }

    return this.http
      .get<any>(`${Backend.HOST_CONTAINER + Backend.WORD}search/?${queryParams}`, this.httpOptions)
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchWordsWithPagination() - success', res)
          // Handle both new pagination format and legacy format
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            // Legacy format - just array
            return {
              results: res,
              hasMore: false,
              totalCount: res.length,
              page: 1,
              pageSize: res.length,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchWordsWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search pairs with pagination support
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of results per page
   * @param filters Optional filters to apply
   * @returns Observable<DataSearchResult[]>
   */
  searchPairsWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
    filters?: any,
  ): Observable<any> {
    this.logger.info('SearchService: searchPairsWithPagination()', query, page, pageSize, filters)

    // Build query parameters
    let queryParams = `page=${page}&pageSize=${pageSize}&word=${query}`

    // Add filters if provided
    if (filters) {
      if (filters.language1) queryParams += `&language1=${filters.language1}`
      if (filters.article1) queryParams += `&article1=${filters.article1}`
      if (filters.partOfSpeech1) queryParams += `&partOfSpeech1=${filters.partOfSpeech1}`
      if (filters.language2) queryParams += `&language2=${filters.language2}`
      if (filters.article2) queryParams += `&article2=${filters.article2}`
      if (filters.partOfSpeech2) queryParams += `&partOfSpeech2=${filters.partOfSpeech2}`
    }

    return this.http
      .get<any>(`${Backend.HOST_CONTAINER + Backend.PAIR}search/?${queryParams}`, this.httpOptions)
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchPairsWithPagination() - success', res)
          // Handle both new pagination format and legacy format
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            // Legacy format - just array
            return {
              results: res,
              hasMore: false,
              totalCount: res.length,
              page: 1,
              pageSize: res.length,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchPairsWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search dictionaries with pagination support
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of results per page
   * @returns Observable<DataSearchResult[]>
   */
  searchDictionariesWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
  ): Observable<any> {
    this.logger.info('SearchService: searchDictionariesWithPagination()', query, page, pageSize)

    return this.http
      .get<any>(
        `${Backend.HOST_CONTAINER + Backend.DIC}search/?page=${page}&pageSize=${pageSize}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchDictionariesWithPagination() - success', res)
          // Handle both new pagination format and legacy format
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            // Legacy format - just array
            return {
              results: res,
              hasMore: false,
              totalCount: res.length,
              page: 1,
              pageSize: res.length,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error(
            'SearchService: searchDictionariesWithPagination() - error:',
            errorMessage,
          )
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search users with pagination support
   * @param query Search query
   * @param page Page number (1-based)
   * @param pageSize Number of results per page
   * @returns Observable<DataSearchResult[]>
   */
  searchUsersWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
  ): Observable<any> {
    this.logger.info('SearchService: searchUsersWithPagination()', query, page, pageSize)

    return this.http
      .get<any>(
        `${Backend.HOST_CONTAINER + Backend.RELATIONSHIP_SEARCH}?page=${page}&pageSize=${pageSize}&user=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchUsersWithPagination() - success', res)
          // Handle both new pagination format and legacy format
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            // Legacy format - just array
            return {
              results: res,
              hasMore: false,
              totalCount: res.length,
              page: 1,
              pageSize: res.length,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchUsersWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search notes with pagination support
   */
  searchNotesWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
  ): Observable<any> {
    this.logger.info('SearchService: searchNotesWithPagination()', query, page, pageSize)

    return this.http
      .get<any>(
        `${Backend.HOST_CONTAINER + Backend.NOTE}search/?page=${page}&pageSize=${pageSize}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchNotesWithPagination() - success', res)
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            return {
              results: Array.isArray(res) ? res : [],
              hasMore: false,
              totalCount: Array.isArray(res) ? res.length : 0,
              page: 1,
              pageSize: Array.isArray(res) ? res.length : 0,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchNotesWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search quizzes with pagination support
   */
  searchQuizzesWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
  ): Observable<any> {
    this.logger.info('SearchService: searchQuizzesWithPagination()', query, page, pageSize)

    return this.http
      .get<any>(
        `${Backend.HOST_CONTAINER + Backend.QUIZ}search/?page=${page}&pageSize=${pageSize}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchQuizzesWithPagination() - success', res)
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            return {
              results: Array.isArray(res) ? res : [],
              hasMore: false,
              totalCount: Array.isArray(res) ? res.length : 0,
              page: 1,
              pageSize: Array.isArray(res) ? res.length : 0,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchQuizzesWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }

  /**
   * Search figures with pagination support
   */
  searchFiguresWithPagination(
    query: string,
    page: number = 1,
    pageSize: number = 20,
  ): Observable<any> {
    this.logger.info('SearchService: searchFiguresWithPagination()', query, page, pageSize)

    return this.http
      .get<any>(
        `${Backend.HOST_CONTAINER + Backend.FIGURE}search/?page=${page}&pageSize=${pageSize}&title=${query}`,
        this.httpOptions,
      )
      .pipe(
        map((res: any) => {
          this.logger.info('SearchService: searchFiguresWithPagination() - success', res)
          if (res.results) {
            return {
              results: res.results,
              hasMore: res.has_more,
              totalCount: res.total_count,
              page: res.page,
              pageSize: res.page_size,
            }
          } else {
            return {
              results: Array.isArray(res) ? res : [],
              hasMore: false,
              totalCount: Array.isArray(res) ? res.length : 0,
              page: 1,
              pageSize: Array.isArray(res) ? res.length : 0,
            }
          }
        }),
        catchError((error: any) => {
          const errorMessage = error.error
          this.logger.error('SearchService: searchFiguresWithPagination() - error:', errorMessage)
          return of({ results: [], hasMore: false, totalCount: 0, page: 1, pageSize: 0 })
        }),
      )
  }
}
